export default function ChildSafety() {
  return (
    <div className="min-h-screen bg-[#FAFAF7] py-12 px-4">
      <div className="max-w-3xl mx-auto bg-white border border-[#E7E5E0] rounded-2xl p-8 space-y-6">
        <h1 className="font-display text-3xl font-semibold">Child Safety Standards</h1>
        <p className="text-sm text-[#6B6B70]">
          Effective date: 29 October 2026 &nbsp;|&nbsp; App: Haven &nbsp;|&nbsp; Developer: PASTPERFECT
        </p>

        <section className="space-y-2">
          <h2 className="font-semibold text-lg">1. Our Commitment</h2>
          <p className="text-sm text-[#404040] leading-relaxed">
            Haven is an adults-only (18+) dating and community platform. We are committed to
            maintaining a safe environment for all users and have a <strong>zero-tolerance policy</strong>{" "}
            toward any form of Child Sexual Abuse and Exploitation (CSAE) and Child Sexual Abuse
            Material (CSAM). This page sets out our publicly accessible Child Safety standards in
            line with Google's Child Safety standards and Google Play policy requirements.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-lg">2. Age Restriction</h2>
          <ul className="list-disc list-inside text-sm text-[#404040] space-y-1">
            <li>Haven is strictly for users aged <strong>18 and over</strong>.</li>
            <li>
              A date of birth is required during onboarding; any date of birth indicating a user
              is under 18 is rejected and the account cannot be created.
            </li>
            <li>
              Accounts suspected of belonging to minors are removed immediately and permanently
              banned.
            </li>
            <li>Users may not misrepresent their age or impersonate a minor.</li>
            <li>We do not permit minors on the platform under any circumstances.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-lg">3. Prohibited Content and Conduct (CSAE)</h2>
          <p className="text-sm text-[#404040] leading-relaxed">
            The following are strictly prohibited on Haven. Violation will result in a permanent
            ban and reporting to relevant authorities.
          </p>
          <ul className="list-disc list-inside text-sm text-[#404040] space-y-1">
            <li>Content that sexually exploits or endangers minors in any way.</li>
            <li>Content that depicts or promotes child sexual abuse or exploitation (CSAE).</li>
            <li>
              Uploading, sharing, requesting, or distributing child sexual abuse material (CSAM)
              in any format — including real, simulated, or AI-generated content.
            </li>
            <li>
              Attempts to groom, contact, or interact with minors for inappropriate or sexual
              purposes.
            </li>
            <li>Sexualised conversations involving minors.</li>
            <li>Sextortion, coercion, or trafficking of minors for sexual purposes.</li>
            <li>
              Sharing or linking to external content that hosts CSAM or facilitates CSAE.
            </li>
            <li>
              Impersonating a minor or presenting as under 18 to circumvent safety measures.
            </li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-lg">4. In-App Reporting</h2>
          <p className="text-sm text-[#404040] leading-relaxed">
            Users can report safety concerns directly in the app. Reporting options include:
          </p>
          <ul className="list-disc list-inside text-sm text-[#404040] space-y-1">
            <li>
              <strong>&ldquo;Child safety / Underage user&rdquo;</strong> — report a profile you
              believe belongs to a minor, or content that endangers a child.
            </li>
            <li>
              <strong>Other safety categories</strong> — nudity, violence, harassment, stigma,
              spam, and other concerns are available from user profiles, photos, stories, and
              chat screens.
            </li>
          </ul>
          <p className="text-sm text-[#404040] leading-relaxed">
            All reports are treated as high priority, reviewed promptly, and handled
            confidentially. If you believe a child is in immediate danger, please contact your
            local emergency services first.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-lg">5. How We Respond to Reports</h2>
          <p className="text-sm text-[#404040] leading-relaxed">
            When we receive a report or otherwise obtain knowledge of potential CSAE/CSAM, we may:
          </p>
          <ul className="list-disc list-inside text-sm text-[#404040] space-y-1">
            <li>Immediately remove the violating content.</li>
            <li>
              Suspend or permanently terminate the involved accounts (no appeal for CSAE
              violations — bans are permanent).
            </li>
            <li>
              Preserve relevant information (account identifiers, message metadata, report
              details) as necessary for compliance and investigations.
            </li>
            <li>Escalate the case for urgent review by our safety team.</li>
            <li>Report confirmed CSAM/CSAE to appropriate authorities.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-lg">6. Reporting to Authorities</h2>
          <p className="text-sm text-[#404040] leading-relaxed">
            Haven complies with all applicable child safety laws and regulations. Where required,
            we will report confirmed CSAM/CSAE to the appropriate authorities, which may include:
          </p>
          <ul className="list-disc list-inside text-sm text-[#404040] space-y-1">
            <li>
              The <strong>National Center for Missing &amp; Exploited Children (NCMEC)</strong>{" "}
              (for reports involving the United States), and/or
            </li>
            <li>
              Relevant <strong>regional or national authorities</strong> depending on the
              jurisdiction, including the South African Police Service (SAPS) and other child
              protection agencies.
            </li>
          </ul>
          <p className="text-sm text-[#404040] leading-relaxed">
            We cooperate fully with law enforcement investigations.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-lg">7. Moderation and Enforcement</h2>
          <p className="text-sm text-[#404040] leading-relaxed">
            We use a combination of automated detection and human moderation. Enforcement actions
            include:
          </p>
          <ul className="list-disc list-inside text-sm text-[#404040] space-y-1">
            <li>Immediate removal of violating content.</li>
            <li>Account suspension or permanent ban.</li>
            <li>Blocking devices or IP addresses.</li>
            <li>Referral to law enforcement where legally required.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-lg">8. Child Safety Point of Contact</h2>
          <p className="text-sm text-[#404040] leading-relaxed">
            For child safety concerns, law enforcement inquiries, or requests related to
            CSAE/CSAM compliance, please contact:
          </p>
          <p className="text-sm text-[#404040] leading-relaxed">
            <strong>Haven Child Safety Team</strong>
            <br />
            Email:{" "}
            <a
              href="mailto:pastperfect@havenpositive.online"
              className="text-purple-600 underline"
            >
              pastperfect@havenpositive.online
            </a>
            <br />
            Please use the subject line <em>&ldquo;Child Safety Report – Haven&rdquo;</em> for
            faster routing.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-lg">9. Scope and Updates</h2>
          <p className="text-sm text-[#404040] leading-relaxed">
            These standards apply to all users and all content on the Haven app and website. We
            may update these standards from time to time and will revise the Effective Date when
            changes are made.
          </p>
        </section>
      </div>
    </div>
  );
}